import { AlertTriangle } from 'lucide-react'
import {
  formatArea,
  formatLength,
  formatMaterialQty,
  formatMoney,
  formatNumber,
} from '@/lib/calculator'
import type { DormerResult, FieldErrors, UnitSystem } from '@/lib/calculator'

interface Props {
  result: DormerResult | null
  unit: UnitSystem
  errors: FieldErrors
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl bg-steel-50 p-3">
      <dt className="text-xs font-semibold uppercase tracking-wide text-steel-500">{label}</dt>
      <dd className="mt-0.5 text-lg font-extrabold text-ink">{value}</dd>
    </div>
  )
}

function ResultsPanel({ result, unit, errors }: Props) {
  if (!result) {
    const count = Object.keys(errors).length
    return (
      <section aria-labelledby="results-heading" data-print="keep" className="card flex min-h-[320px] flex-col items-center justify-center p-8 text-center">
        <AlertTriangle className="mb-3 h-10 w-10 text-signal-dark" aria-hidden="true" />
        <h2 id="results-heading" className="text-xl">
          Waiting for valid measurements
        </h2>
        <p className="mt-2 max-w-sm text-steel-600" role="status">
          {count} {count === 1 ? 'field needs' : 'fields need'} attention. Fix the highlighted inputs and your estimate appears instantly.
        </p>
      </section>
    )
  }

  return (
    <section aria-labelledby="results-heading" data-print="keep" className="space-y-5" aria-live="polite">
      <div className="overflow-hidden rounded-2xl bg-ink text-white shadow-lift">
        <div className="blueprint-grid p-6 sm:p-7">
          <h2 id="results-heading" className="text-sm font-bold uppercase tracking-widest !text-blueprint-200">
            Estimated total cost
          </h2>
          <p className="mt-2 text-4xl font-extrabold text-white sm:text-5xl">{formatMoney(result.total)}</p>
          <p className="mt-2 text-blueprint-100">
            Likely range <strong className="text-signal">{formatMoney(result.low)}</strong> to{' '}
            <strong className="text-signal">{formatMoney(result.high)}</strong>
          </p>
          <p className="mt-1 text-sm text-blueprint-200">
            About {formatMoney(result.costPerSqFtFloor)} per sq ft of new floor space.
          </p>
        </div>
      </div>

      <div className="card p-5 sm:p-6">
        <h3 className="mb-3 text-lg">Dimensions and areas</h3>
        <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <Stat label="Floor area" value={formatArea(result.floorArea, unit)} />
          <Stat label="Roof area" value={formatArea(result.roofArea, unit)} />
          <Stat label="Wall area" value={formatArea(result.wallArea, unit)} />
          <Stat label="Roof rise" value={formatLength(result.rise, unit)} />
          <Stat label="Rafter length" value={formatLength(result.slopeLength, unit)} />
          <Stat label="Pitch angle" value={`${formatNumber(result.pitchDegrees, 1)}\u00B0`} />
        </dl>
      </div>

      <div className="card p-5 sm:p-6">
        <h3 className="mb-3 text-lg">Materials needed</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm sm:text-base">
            <caption className="sr-only">Materials list with quantities</caption>
            <thead>
              <tr className="border-b-2 border-steel-200 text-xs uppercase tracking-wide text-steel-500">
                <th scope="col" className="py-2 pr-3 font-bold">Item</th>
                <th scope="col" className="py-2 text-right font-bold">Quantity</th>
              </tr>
            </thead>
            <tbody>
              {result.materials.map(m => (
                <tr key={m.name} className="border-b border-steel-100 last:border-0">
                  <th scope="row" className="py-2.5 pr-3 font-semibold text-ink">
                    {m.name}
                    <span className="block text-xs font-normal text-steel-500">{m.detail}</span>
                  </th>
                  <td className="py-2.5 text-right font-bold tabular-nums text-ink">{formatMaterialQty(m, unit)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card p-5 sm:p-6">
        <h3 className="mb-3 text-lg">Cost breakdown</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm sm:text-base">
            <caption className="sr-only">Cost breakdown by work item</caption>
            <thead className="sr-only">
              <tr>
                <th scope="col">Work item</th>
                <th scope="col">Cost</th>
              </tr>
            </thead>
            <tbody>
              {result.costs.map(c => (
                <tr key={c.label} className="border-b border-steel-100">
                  <th scope="row" className="py-2.5 pr-3 font-medium text-steel-700">{c.label}</th>
                  <td className="py-2.5 text-right tabular-nums text-ink">{formatMoney(c.amount)}</td>
                </tr>
              ))}
              <tr className="border-b border-steel-100">
                <th scope="row" className="py-2.5 pr-3 font-medium text-steel-700">Contingency (10%)</th>
                <td className="py-2.5 text-right tabular-nums text-ink">{formatMoney(result.contingency)}</td>
              </tr>
              <tr className="border-b-2 border-steel-200">
                <th scope="row" className="py-2.5 pr-3 font-medium text-steel-700">Design and permits (5%)</th>
                <td className="py-2.5 text-right tabular-nums text-ink">{formatMoney(result.permits)}</td>
              </tr>
              <tr>
                <th scope="row" className="py-3 pr-3 text-lg font-extrabold text-ink">Estimated total</th>
                <td className="py-3 text-right text-lg font-extrabold tabular-nums text-ink">{formatMoney(result.total)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-steel-500">
          Planning estimate using average US installed prices. Not a quote. See the disclaimer for details.
        </p>
      </div>
    </section>
  )
}

export default ResultsPanel
